import React, { useEffect, useState, useContext } from 'react';
import { Box, Switch } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  ICompany,
  useMembershipCompaniesQuery,
  useUpdateCompanyMainMemberForMembershipMutation
} from '../../../generated/graphql';
import { canUserEdit } from '../../../utils/rightUtils';
import { FormatTableData } from '../../../utils/styleUtils';
import PaperCard from '../../Common/Layout/PaperCard';
import DenseTable from '../../Common/Tables/DenseTable';
import { IHeadCell } from '../../Common/Tables/OverviewTable';
import MembershipAddCompany from './MembershipAddCompany';
import { updateCompanyInCache } from '../../../utils/clientUtils';

type IProps = {
  membershipId: number;
};

const CompaniesTab: React.FunctionComponent<IProps> = ({ membershipId }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [openNewCompany, setOpenNewCompany] = useState(false);
  const { data, loading } = useMembershipCompaniesQuery({
    variables: { membership_id: membershipId },
    fetchPolicy: 'no-cache'
  });
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [updateMainMember, updateMainMemberResult] =
    useUpdateCompanyMainMemberForMembershipMutation();
  const history = useNavigate();
  const canEdit = canUserEdit(state.user, Types.Membership);

  useEffect(() => {
    if (data && data.result) {
      setCompanies(data.result as ICompany[]);
    }
  }, [data]);

  useEffect(() => {
    if (updateMainMemberResult.error) {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: updateMainMemberResult.error.message,
          autoHide: 6000
        }
      });
    }
  }, [updateMainMemberResult.error]);

  const handleChangeMainMember = (rowData: ICompany) => {
    const main = !rowData.is_main_member;
    updateMainMember({
      variables: {
        id: parseInt(rowData.id as string),
        membership_id: parseInt(membershipId.toString()),
        is_main_member: main
      },
      update() {
        const updatedCompany = { ...rowData, is_main_member: main };
        updateCompanyInCache(updatedCompany);
        const message = main
          ? `Company ${rowData.name} set as main company`
          : `Company ${rowData.name} no longer main`;
        const updatedCompanies = companies.map((c) =>
          c.id === rowData.id
            ? { ...rowData, is_main_member: main }
            : main
            ? { ...c, is_main_member: false }
            : c
        );
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: message
          }
        });
        setCompanies(updatedCompanies);
      }
    });
  };

  const isMainStatus = (rowData: ICompany) => (
    <Switch
      id="is_main_member_switch"
      disableRipple
      name="checkIsMainMember"
      disabled={!canEdit}
      onChange={() => handleChangeMainMember(rowData)}
      checked={rowData.is_main_member || false}
    />
  );

  const handleDetails = (rowData: ICompany) => {
    dispatch({
      type: Actions.SetSelectedContact,
      payload: { name: 'contact name', company_name: rowData.name }
    });
    history(`/companies/${rowData.id}`, { state: { ...rowData } });
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 51
    },
    {
      title: 'Main',
      field: 'is_main_member',
      sorting: false,
      render: isMainStatus,
      width: 90,
      type: 'boolean'
    },
    {
      title: 'Company Name',
      field: 'name',
      width: 386
    },
    {
      title: 'Street',
      field: 'main_address.street',
      width: 322
    },
    {
      title: 'City',
      field: 'main_address.city',
      width: 179
    },
    {
      title: 'Country',
      field: 'main_address.country.name',
      width: 276
    }
  ] as IHeadCell[];

  const handleNewCompany = (company: ICompany) => {
    setCompanies([company, ...(companies || [])]);
  };

  return (
    <Box className="centralize" sx={{ width: '100%', paddingTop: '80px' }}>
      <PaperCard
        headerText="Companies"
        width="82%"
        createButtonProps={{
          id: 'Add-Company-Button-Companies-Tab',
          onClick: () => setOpenNewCompany(true),
          text: '+ Add Company'
        }}
        additionalStyle={{ height: '680px' }}
      >
        <DenseTable
          columns={columns}
          data={FormatTableData(companies)}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={() => true}
          pageSize={10}
          crudType={Types.Membership}
          additionalToolbar={true}
          fixedHeaders={true}
        />
        {openNewCompany && (
          <MembershipAddCompany
            handleClose={() => setOpenNewCompany(false)}
            membershipId={membershipId}
            addNewCompany={handleNewCompany}
          />
        )}
      </PaperCard>
    </Box>
  );
};

export default CompaniesTab;
